<script setup lang="ts">
  import { computed, ref } from 'vue';

  import {
    DatePicker,
    IconImage,
    ThemeProvider,
    FilterModal,
    SearchCombobox,
    NumberStepperInput,
  } from '@/shared/ui';
  import { NotificationPeriodicity, formatDateToPtBR } from '@/shared/lib';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';

  import { NotificationFilterFormData } from '../model';
  import { useNotificationFilterModal } from '../model/useNotificationFilterModal';
  import type { StatusOption } from '../model/filter.types';

  interface User {
    name: string;
    id: number;
  }

  const props = defineProps<{
    currentFilters?: Partial<NotificationFilterFormData>;
    defaultCustomPeriodicity?: number | null;
    statusOptions: StatusOption[];
    showCreatedBy?: boolean;
    usersList?: User[];
    usersLoading?: boolean;
    usersError?: string | null;
    gridBorderClass?: string;
    dateSectionClass?: string;
    showDateSectionTitle?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: Partial<NotificationFilterFormData>): void;
    (e: 'clear-filters'): void;
  }>();

  const periodicityOptions = [
    { value: NotificationPeriodicity.DAILY, label: 'Diária' },
    { value: NotificationPeriodicity.WEEKLY, label: 'Semanal' },
    { value: NotificationPeriodicity.BIWEEKLY, label: 'Quinzenal' },
    { value: NotificationPeriodicity.MONTHLY, label: 'Mensal' },
    { value: NotificationPeriodicity.CUSTOM, label: 'Personalizada' },
  ];

  const {
    formData,
    isStartDateOpen,
    isEndDateOpen,
    hasInvalidDateRange,
    handleClearFilters,
    applyFilters,
    selectPeriodicity,
    selectStatus,
  } = useNotificationFilterModal({
    defaultCustomPeriodicity:
      props.defaultCustomPeriodicity === undefined ? 1 : props.defaultCustomPeriodicity,
    currentFilters: props.currentFilters,
    onFilterUpdate: (filters) => {
      emit('update:filter', filters);
      emit('close');
    },
    onClearFilters: () => {
      emit('clear-filters');
    },
  });

  const handleApplyFilters = () => {
    applyFilters();
  };

  const userSearchQuery = ref('');

  const usersListOptions = computed(() => {
    if (!props.usersList) return [];
    return props.usersList
      .map((user) => ({
        value: String(user.id),
        label: user.name || 'Nome não disponível',
      }))
      .sort((first, second) => first.label.localeCompare(second.label, 'pt-BR'));
  });

  const filteredUsersOptions = computed(() => {
    const query = userSearchQuery.value.trim().toLowerCase();
    if (!query) return usersListOptions.value;
    return usersListOptions.value.filter((option) => option.label.toLowerCase().includes(query));
  });
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @clear-filters="handleClearFilters"
    @apply-filters="handleApplyFilters"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="space-y-1 text-sm">
          <div :class="['grid grid-cols-2 gap-6', gridBorderClass]">
            <!-- Periodicidade Section -->
            <div class="py-6 pl-6">
              <h4 class="font-medium text-gray-07 pb-4">
                Periodicidade
              </h4>
              <div class="flex flex-col gap-2">
                <button
                  v-for="option in periodicityOptions"
                  :key="option.value"
                  :class="[
                    'py-2 px-4 rounded-lg text-left transition-colors',
                    formData.periodicity === option.value
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07 hover:bg-gray-100',
                  ]"
                  @click="selectPeriodicity(option.value)"
                >
                  {{ option.label }}
                </button>

                <!-- Custom Periodicity Controls -->
                <div
                  v-if="formData.periodicity === NotificationPeriodicity.CUSTOM"
                  class="mt-3 rounded-lg"
                >
                  <div class="flex items-center mt-1 gap-1">
                    <span class="text-gray-07">A cada</span>

                    <NumberStepperInput
                      v-model="formData.customPeriodicity"
                      :min="1"
                      :step="1"
                      placeholder="Dias"
                      :null-on-decrement-below-min="true"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Status Section -->
            <div class="py-6 pr-6">
              <h4 class="font-medium text-gray-07 pb-4">
                Status
              </h4>
              <div class="flex flex-col gap-1">
                <button
                  v-for="option in statusOptions"
                  :key="option.value"
                  :class="[
                    'py-2 px-4 rounded-lg text-left transition-colors',
                    formData.status === option.value
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07 hover:bg-gray-100',
                  ]"
                  @click="selectStatus(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- Colaborators Section -->
          <div
            v-if="showCreatedBy && usersList"
            class="py-8 px-4 w-full border-b border-gray-02"
          >
            <h4 class="font-medium text-gray-07 mb-2">
              Criado por:
            </h4>
            <SearchCombobox
              :model-value="formData.createdBy != null ? String(formData.createdBy) : ''"
              :options="filteredUsersOptions"
              :loading="usersLoading"
              :empty-message="usersError ?? 'Nenhum colaborador encontrado.'"
              placeholder="Selecione um colaborador"
              search-placeholder="Buscar colaborador..."
              class="w-full"
              @update:model-value="formData.createdBy = $event ? Number($event) : null"
              @search="userSearchQuery = $event"
            />
          </div>

          <!-- Date Range Section -->
          <div :class="['px-6', dateSectionClass]">
            <h4
              v-if="showDateSectionTitle"
              class="font-medium text-gray-07 pb-4"
            >
              Período
            </h4>
            <div class="block text-sm font-medium text-gray-07 mb-4">
              Intervalo de busca
            </div>
            <div class="flex gap-6">
              <div class="w-1/2">
                <div class="block text-sm font-medium text-gray-07 mb-2">
                  A partir de:
                </div>
                <button
                  class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                  @click="isStartDateOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5 mr-2"
                  />
                  <span class="grow text-left">{{
                    formData.startDate !== null
                      ? formatDateToPtBR(formData.startDate)
                      : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  v-model:selected-date="formData.startDate"
                  v-model:is-open="isStartDateOpen"
                  class="my-auto"
                  @apply="isStartDateOpen = false"
                  @cancel="isStartDateOpen = false"
                />
              </div>

              <div class="w-1/2">
                <div class="block text-sm font-medium text-gray-07 mb-2">
                  Até:
                </div>
                <button
                  class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium w-full"
                  @click="isEndDateOpen = true"
                >
                  <IconImage
                    :src="CalendarIcon"
                    alt="Calendário"
                    class="h-5 w-5 mr-2"
                  />
                  <span class="grow text-left">{{
                    formData.endDate !== null
                      ? formatDateToPtBR(formData.endDate)
                      : 'Selecione uma data'
                  }}</span>
                </button>
                <DatePicker
                  v-model:selected-date="formData.endDate"
                  v-model:is-open="isEndDateOpen"
                  :min-date="formData.startDate ?? undefined"
                  class="my-auto"
                  @apply="isEndDateOpen = false"
                  @cancel="isEndDateOpen = false"
                />
              </div>
            </div>
            <p
              v-if="hasInvalidDateRange"
              class="mt-3 text-sm text-red-600"
            >
              A data final deve ser maior ou igual à data inicial.
            </p>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
